/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import Modal from "@/components/ui/modal";
import Input from "@/components/ui/input";
import Button from "@/components/ui/button";
import FileInput from "@/components/ui/file-input";
import { Close, Edit } from "@/components/icons";
import { getFileStreamUrl } from "../../services/education-catalog.api";
import {
  educationVideosApi,
  type EducationVideo,
  type EducationVideoListMeta,
} from "../../services/education-videos.api";
import styles from "./EducationVideoPoolModal.module.scss";


const thumbnailCache = new Map<string, string>();
interface EducationVideoPoolModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect?: (video: EducationVideo) => void;
  mode?: "manage" | "select";
}

function formatDate(value?: string): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("tr-TR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function formatDuration(minutes?: number | null): string {
  if (minutes == null || !Number.isFinite(minutes)) return "-";
  if (minutes < 60) return `${Math.round(minutes)} dk`;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  return rest > 0 ? `${hours} sa ${rest} dk` : `${hours} sa`;
}

function defaultMeta(page: number, limit: number, total: number): EducationVideoListMeta {
  const totalPages = Math.max(1, Math.ceil(total / limit));
  return {
    page,
    limit,
    total,
    totalPages,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };
}

function readErrorMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  if (!("response" in error)) return undefined;
  const response = error.response;
  if (!response || typeof response !== "object" || !("data" in response)) {
    return undefined;
  }
  const data = response.data;
  if (!data || typeof data !== "object") return undefined;
  if ("message" in data && typeof data.message === "string" && data.message.trim()) {
    return data.message.trim();
  }
  if ("messages" in data && Array.isArray(data.messages)) {
    const first = data.messages.find(
      (item): item is string => typeof item === "string" && item.trim().length > 0,
    );
    if (first) return first.trim();
  }
  return undefined;
}

function getVideoPoolErrorMessage(
  error: unknown,
  t: (key: string) => string,
): string | undefined {
  const raw = readErrorMessage(error);
  if (!raw) return undefined;
  const map: Record<string, string> = {
    FILE_NOT_FOUND: t("createEducation.educationVideos.errors.fileNotFound"),
    EDUCATION_VIDEO_FILE_MUST_BE_VIDEO: t("createEducation.educationVideos.errors.fileMustBeVideo"),
    EDUCATION_VIDEO_NOT_FOUND: t("createEducation.educationVideos.errors.videoNotFound"),
    EDUCATION_VIDEO_POOL_FILE_REQUIRED: t("createEducation.educationVideos.errors.poolFileRequired"),
    OBJECTIVE_VIDEO_QUESTIONS_REQUIRE_VIDEO: t("createEducation.educationVideos.errors.questionsRequireVideo"),
  };
  return map[raw] ?? raw;
}

function VideoPreview(props: { fileId?: string; fallback: string }) {
  const { fileId, fallback } = props;
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!fileId) {
      setThumbnailUrl(null);
      return;
    }
    const cached = thumbnailCache.get(fileId);
    if (cached) {
      setThumbnailUrl(cached);
      return;
    }

    let cancelled = false;
    let streamUrl: string | null = null;
    let thumbnailObjectUrl: string | null = null;
    const video = document.createElement("video");
    const canvas = document.createElement("canvas");

    const cleanup = () => {
      video.pause();
      video.removeAttribute("src");
      video.load();
      if (streamUrl) URL.revokeObjectURL(streamUrl);
      if (cancelled && thumbnailObjectUrl) URL.revokeObjectURL(thumbnailObjectUrl);
    };

    getFileStreamUrl(fileId)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        streamUrl = url;
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.src = url;

        const captureFrame = () => {
          const width = video.videoWidth || 160;
          const height = video.videoHeight || 90;
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            setThumbnailUrl(null);
            cleanup();
            return;
          }
          ctx.drawImage(video, 0, 0, width, height);
          canvas.toBlob((blob) => {
            if (!blob) {
              cleanup();
              return;
            }
            const objectUrl = URL.createObjectURL(blob);
            thumbnailObjectUrl = objectUrl;
            thumbnailCache.set(fileId, objectUrl);
            if (!cancelled) {
              setThumbnailUrl(objectUrl);
            }
            cleanup();
          }, "image/jpeg", 0.82);
        };

        video.onloadeddata = () => {
          const targetTime =
            Number.isFinite(video.duration) && video.duration > 1 ? 1 : 0;
          if (targetTime === 0) {
            captureFrame();
            return;
          }
          video.currentTime = targetTime;
        };
        video.onseeked = captureFrame;
        video.onerror = cleanup;
      })
      .catch(() => {
        if (!cancelled) setThumbnailUrl(null);
      });

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [fileId]);

  if (!thumbnailUrl) {
    return (
      <span className={styles.videoThumbFallback}>
        {fallback.slice(0, 1).toUpperCase()}
      </span>
    );
  }

  return (
    <div className={styles.videoThumbFrame}>
      <img className={styles.videoThumbMedia} src={thumbnailUrl} alt={fallback} />
      <span className={styles.videoThumbPlay}>▶</span>
    </div>
  );
}

const PAGE_LIMIT = 10;

const EducationVideoPoolModal: React.FC<EducationVideoPoolModalProps> = ({
  isOpen,
  onClose,
  onSelect,
  mode = "manage",
}) => {
  const { t } = useTranslation();
  const isSelectMode = mode === "select";
  const [videos, setVideos] = useState<EducationVideo[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [uploadedFileID, setUploadedFileID] = useState("");
  const [editing, setEditing] = useState<EducationVideo | null>(null);
  const [selectedVideoId, setSelectedVideoId] = useState("");
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState<EducationVideoListMeta>(defaultMeta(1, PAGE_LIMIT, 0));

  const selectedVideo = useMemo(
    () => videos.find((video) => video.id === selectedVideoId) ?? null,
    [selectedVideoId, videos],
  );

  const resetForm = useCallback(() => {
    setName("");
    setUploadedFileID("");
    setEditing(null);
  }, []);

  const loadVideos = useCallback(
    async (targetPage: number) => {
      setLoading(true);
      try {
        const res = await educationVideosApi.list({ page: targetPage, limit: PAGE_LIMIT });
        setVideos(res.items);
        const nextMeta = res.meta ?? defaultMeta(targetPage, PAGE_LIMIT, res.count);
        setMeta(nextMeta);
        setSelectedVideoId((prev) => {
          if (prev && res.items.some((video) => video.id === prev)) return prev;
          return res.items[0]?.id ?? "";
        });
      } catch (error) {
        toast.error(
          getVideoPoolErrorMessage(error, t) ??
            t("createEducation.educationVideos.loadError"),
        );
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      setPage(1);
      return;
    }
    resetForm();
    setPage(1);
  }, [isOpen, resetForm]);

  useEffect(() => {
    if (!isOpen) return;
    void loadVideos(page);
  }, [isOpen, page, loadVideos]);

  function handleSelectVideo(videoId: string) {
    setSelectedVideoId(videoId);
    if (editing?.id && editing.id !== videoId) resetForm();
  }

  function startEdit(video: EducationVideo) {
    setSelectedVideoId(video.id);
    setEditing(video);
    setName(video.name);
    setUploadedFileID("");
  }

  async function handleSave() {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error(t("createEducation.educationVideos.nameRequired"));
      return;
    }
    if (!editing && !uploadedFileID) {
      toast.error(t("createEducation.educationVideos.fileRequired"));
      return;
    }

    setSaving(true);
    try {
      if (editing) {
        await educationVideosApi.update(editing.id, {
          name: trimmedName,
          ...(uploadedFileID ? { fileID: uploadedFileID } : {}),
        });
        toast.success(t("createEducation.educationVideos.updateSuccess"));
      } else {
        await educationVideosApi.create({
          name: trimmedName,
          fileID: uploadedFileID,
        });
        toast.success(t("createEducation.educationVideos.createSuccess"));
      }
      resetForm();
      void loadVideos(page);
    } catch (error) {
      toast.error(
        getVideoPoolErrorMessage(error, t) ??
          t("createEducation.educationVideos.saveError"),
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(video: EducationVideo) {
    setSaving(true);
    try {
      await educationVideosApi.remove(video.id);
      toast.success(t("createEducation.educationVideos.deleteSuccess"));
      if (editing?.id === video.id) resetForm();
      const fallbackPage = videos.length === 1 && page > 1 ? page - 1 : page;
      if (fallbackPage !== page) {
        setPage(fallbackPage);
      } else {
        void loadVideos(fallbackPage);
      }
    } catch (error) {
      toast.error(
        getVideoPoolErrorMessage(error, t) ??
          t("createEducation.educationVideos.deleteError"),
      );
    } finally {
      setSaving(false);
    }
  }

  const customFooter = (
    <div className={styles.footer}>
      <div className={styles.pagination}>
        <Button
          variant="outlined"
          color="default"
          size="small"
          disabled={!meta.hasPreviousPage || loading}
          onClick={() => setPage((prev) => Math.max(1, prev - 1))}
        >
          {t("createEducation.educationVideos.previousPage")}
        </Button>
        <span className={styles.paginationLabel}>
          {t("createEducation.educationVideos.pageStatus", {
            page: meta.page,
            totalPages: Math.max(meta.totalPages, 1),
            total: meta.total,
          })}
        </span>
        <Button
          variant="outlined"
          color="default"
          size="small"
          disabled={!meta.hasNextPage || loading}
          onClick={() => setPage((prev) => prev + 1)}
        >
          {t("createEducation.educationVideos.nextPage")}
        </Button>
      </div>
      <div className={styles.footerActions}>
        <Button variant="text" color="secondary" onClick={onClose}>
          {t("buttons.close")}
        </Button>
        {isSelectMode && onSelect && (
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedVideo}
            onClick={() => selectedVideo && onSelect(selectedVideo)}
          >
            {t("createEducation.educationVideos.addSelected")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isSelectMode
          ? t("createEducation.educationVideos.selectTitle")
          : t("createEducation.educationVideos.title")
      }
      size="fullscreen"
      customFooter={customFooter}
    >
      <div className={`${styles.content} ${isSelectMode ? styles.contentSelect : ""}`}>
        {!isSelectMode && (
          <section className={styles.formSection}>
            <h4>
              {editing
                ? t("createEducation.educationVideos.editTitle")
                : t("createEducation.educationVideos.newTitle")}
            </h4>
            <Input
              label={`${t("createEducation.educationVideos.videoName")}*`}
              placeholder={t("createEducation.educationVideos.videoNamePlaceholder")}
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
            <FileInput
              label={
                editing
                  ? t("createEducation.educationVideos.replaceVideo")
                  : `${t("createEducation.educationVideos.uploadVideo")}*`
              }
              placeholder={t("createEducation.educationVideos.dropzoneText")}
              accept=".mp4,.webm,.mov,.mkv,.avi,.m4v,.ogv,.ogg,video/*"
              supportedFormats={["MP4", "WEBM", "MOV", "MKV", "AVI", "M4V", "OGV", "OGG"]}
              maxSize={500}
              maxFiles={1}
              uploadToBackend
              onUploadSuccess={(fileId) => setUploadedFileID(fileId)}
              onFileRemove={() => setUploadedFileID("")}
              enableDemoProgress={false}
              fileType="file"
            />
            <div className={styles.formActions}>
              {editing && (
                <Button variant="outlined" color="default" onClick={resetForm}>
                  {t("buttons.cancel")}
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSave}
                loading={saving}
              >
                {editing
                  ? t("createEducation.educationVideos.update")
                  : t("createEducation.educationVideos.save")}
              </Button>
            </div>
          </section>
        )}

        <section className={styles.listSection}>
          <div className={styles.listHeader}>
            <div>
              <h4>{t("createEducation.educationVideos.listTitle")}</h4>
              <p className={styles.listHint}>
                {isSelectMode
                  ? t("createEducation.educationVideos.selectHint")
                  : t("createEducation.educationVideos.manageHint")}
              </p>
            </div>
          </div>
          {loading ? (
            <p className={styles.empty}>{t("common.loading")}</p>
          ) : videos.length === 0 ? (
            <p className={styles.empty}>{t("createEducation.educationVideos.empty")}</p>
          ) : (
            <div className={styles.videoGrid}>
              {videos.map((video) => (
                <div
                  key={video.id}
                  className={`${styles.videoCard} ${selectedVideoId === video.id ? styles.videoCardSelected : ""}`}
                >
                  <button
                    type="button"
                    className={styles.videoCardBody}
                    onClick={() => handleSelectVideo(video.id)}
                  >
                    {onSelect && (
                      <input
                        type="radio"
                        name="education-video-pool"
                        checked={selectedVideoId === video.id}
                        onChange={() => handleSelectVideo(video.id)}
                      />
                    )}
                    <div className={styles.videoPreviewWrap}>
                      <VideoPreview
                        fileId={video.fileID}
                        fallback={video.file?.originalName ?? video.name}
                      />
                    </div>
                    <div className={styles.videoInfo}>
                      <span className={styles.videoName}>{video.name}</span>
                      <span className={styles.videoMeta}>
                        {video.file?.originalName ?? "-"}
                      </span>
                      <span className={styles.videoMeta}>
                        {formatDuration(video.file?.durationMinutes)} · {formatDate(video.updatedAt)}
                      </span>
                    </div>
                  </button>
                  {!isSelectMode && (
                    <div className={styles.videoActions}>
                      <button
                        type="button"
                        className={styles.iconButton}
                        onClick={() => startEdit(video)}
                        aria-label={t("createEducation.educationVideos.edit")}
                      >
                        <Edit width={16} height={16} />
                      </button>
                      <button
                        type="button"
                        className={styles.deleteButton}
                        onClick={() => void handleDelete(video)}
                        aria-label={t("createEducation.educationVideos.delete")}
                      >
                        <Close width={16} height={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
};

export default EducationVideoPoolModal;
